import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Confirm } from "@/components/common";
import { api, errMsg, money } from "@/lib/api";

const STATUS = { trial: ["Deneme", "bg-orange-100 text-orange-800"], active: ["Aktif", "bg-teal-100 text-teal-800"], expired: ["Süresi doldu", "bg-red-100 text-red-700"] };

function Row({ t, onChange }) {
  const [plan, setPlan] = useState(t.plan !== "none" && t.status === "active" ? t.plan : "standart");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [label, cls] = STATUS[t.status] || [t.status, ""];
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${t.id}/plan`, { plan, days: Number(days) });
      toast.success(`${t.name}: ${plan} planı ${days} gün tanımlandı`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <tr className="border-t align-top" data-testid={`tenant-row-${t.id}`}>
      <td className="py-3 pr-3">
        <div className="font-semibold">{t.name}</div>
        <div className="text-xs text-slate-500">{t.owner_email}{t.phone ? ` · ${t.phone}` : ""}</div>
        <div className="text-xs text-slate-400">Kayıt: {new Date(t.created_at).toLocaleDateString("tr-TR")} · {t.counts.users} kullanıcı · {t.counts.accounts} cari · {t.counts.invoices} fatura</div>
      </td>
      <td className="py-3 pr-3">
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${cls}`} data-testid={`tenant-status-${t.id}`}>{label}</span>
        {t.converted && <span className="ml-1 rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`tenant-converted-${t.id}`}>Ödedi ×{t.payments}</span>}
        <div className="mt-1 text-xs text-slate-600">{t.plan_name}{t.days_left != null ? ` · ${t.days_left} gün` : ""}</div>
      </td>
      <td className="py-3">
        <div className="flex flex-wrap items-center gap-2">
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="h-9 border bg-white px-2 text-sm" data-testid={`tenant-plan-select-${t.id}`}>
            <option value="standart">Standart</option><option value="plus">Plus</option><option value="pro">Pro</option>
          </select>
          <input type="number" min={1} max={366} value={days} onChange={(e) => setDays(e.target.value)} className="h-9 w-20 border px-2 text-sm" data-testid={`tenant-days-input-${t.id}`} />
          <Button size="sm" onClick={save} disabled={busy} className="rounded-[4px]" data-testid={`tenant-plan-save-${t.id}`}>Tanımla</Button>
          <Confirm title="Şirket kalıcı olarak silinsin mi?" description={`${t.name} ve tüm verileri (kullanıcılar, cariler, faturalar) geri alınamaz şekilde silinir.`} onConfirm={async () => { try { await api.delete(`/admin/tenants/${t.id}`); toast.success("Şirket silindi"); onChange(); } catch (e) { toast.error(errMsg(e)); } }} testid={`tenant-delete-confirm-${t.id}`}
            trigger={<Button size="sm" variant="ghost" className="rounded-[4px] text-red-700 hover:bg-red-50" data-testid={`tenant-delete-${t.id}`}><Trash /></Button>} />
        </div>
      </td>
    </tr>
  );
}

export default function Tenants() {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const load = () => Promise.all([api.get("/admin/tenants"), api.get("/admin/tenants/stats")]).then(([r, s]) => { setRows(r.data); setStats(s.data); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="tenants-page">
      <PageHeader eyebrow="Yönetim / Platform" title="Müşteri Şirketleri" />
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="tenant-stats">
          <Kpi label="Toplam Kayıt" value={stats.total} testid="tenant-stat-total" />
          <Kpi label="Denemede" value={stats.trial} tone="amber" testid="tenant-stat-trial" delay={30} />
          <Kpi label="Ödeyen (aktif)" value={stats.active} tone="green" testid="tenant-stat-active" delay={60} />
          <Kpi label="Süresi Dolan" value={stats.expired} tone="red" testid="tenant-stat-expired" delay={90} />
          <Kpi label="Dönüşüm Oranı" value={`%${stats.conversion_rate}`} tone="green" sub={`${stats.converted} şirket ödemeye geçti`} testid="tenant-stat-conversion" delay={120} />
          <Kpi label="Son 30 Gün Gelir" value={money(stats.revenue_last_30d)} sub={`${stats.new_last_30d} yeni kayıt`} testid="tenant-stat-revenue" delay={150} />
        </div>
      )}
      <div className="border bg-white p-5">
        <p className="flex items-center gap-2 text-sm text-slate-600"><Buildings size={18} /> Kayıt olan deneme şirketleri. Havale/EFT ile ödeyenlere buradan plan ve süre tanımlayın; süre mevcut bitişe eklenir.</p>
        {rows === null ? <p className="mt-4 text-sm text-slate-500">Yükleniyor…</p> : rows.length === 0 ? (
          <p className="mt-4 text-sm text-slate-500" data-testid="tenants-empty">Henüz kayıt olan şirket yok.</p>
        ) : (
          <table className="mt-4 w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="pb-2">Şirket</th><th className="pb-2">Durum</th><th className="pb-2">Plan tanımla</th></tr></thead>
            <tbody>{rows.map((t) => <Row key={t.id} t={t} onChange={load} />)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
